"use client";

import {
	BILLING_INTERVALS,
	type BillingInterval,
} from "@saas/payments/lib/pricing-v2";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import { useTranslations } from "next-intl";

const LABEL_KEYS = {
	month: "pricing.monthly",
	quarter: "pricing.quarterly",
	year: "pricing.yearly",
} as const;

/**
 * Monthly / quarterly / yearly switch for the v2 plans and add-ons. The
 * active segment sits on the page background (not primary) so the savings
 * badge stays readable on it. Below `sm` each badge stacks under its label,
 * so three options fit a 390px phone without wrapping.
 */
export function BillingToggle({
	value,
	onChange,
	className,
}: {
	value: BillingInterval;
	onChange: (value: BillingInterval) => void;
	className?: string;
}) {
	const t = useTranslations();
	return (
		<div className={cn("flex", className)}>
			<fieldset
				className="inline-flex items-stretch gap-1 rounded-2xl border bg-muted p-1 sm:rounded-full"
				data-test="price-table-interval-tabs"
			>
				<legend className="sr-only">
					{t("pricing.v2.billingPeriod")}
				</legend>
				{BILLING_INTERVALS.map((o) => {
					const active = o.id === value;
					return (
						<button
							key={o.id}
							type="button"
							aria-pressed={active}
							onClick={() => onChange(o.id)}
							className={cn(
								"flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors sm:flex-row sm:gap-1.5 sm:rounded-full sm:px-4",
								active
									? "bg-background text-foreground shadow-sm ring-1 ring-primary"
									: "text-muted-foreground hover:text-foreground",
							)}
						>
							{t(LABEL_KEYS[o.id])}
							{o.savingsPercent ? (
								<Badge
									status="success"
									className="px-1.5 py-0.5 text-[10px]"
								>
									{t("pricing.v2.save", {
										percent: o.savingsPercent,
									})}
								</Badge>
							) : null}
						</button>
					);
				})}
			</fieldset>
		</div>
	);
}
